/**
 * Drag-out room (PRP-SKETCH-FIELD-2 E10): from a selected wall or wall segment
 * (a segment is the wall between two breaks, which the core stores as its own
 * wall), drag the "+" handle outward and a room the full length of that wall
 * follows the finger. The screen calls `dragOutPreview` while dragging and
 * `dragOutRoom` on release.
 */
import { POLICY } from './policy';
import { dist, edgeOf, findRoom, outN, snapIn, tidy } from './geom';
import { roomOffWall } from './rooms';
import { wallFtOf } from './thickness';
import type { Fail, Plan, Pt } from './types';

export interface DragOutPreview {
  /** Depth snapped to whole inches, feet (the live label). */
  depthFt: number;
  /** The new room's outline, for drawing while dragging. */
  outline: Pt[];
}

/** The room a drag of `depthFt` would make (null when the wall is gone or the depth is too small). */
export function dragOutPreview(plan: Plan, rid: string, vid: number, depthFt: number): DragOutPreview | null {
  const r = findRoom(plan, rid);
  const E = r && edgeOf(r, vid);
  const d = snapIn(depthFt);
  if (!r || !E || d < POLICY.across_min_ft) return null;
  const n = outN(r, E.a, E.b), T = wallFtOf(r, vid);
  const off = (p: Pt, k: number) => ({ x: p.x + n.x * k, y: p.y + n.y * k });
  return { depthFt: d, outline: [off(E.a, T), off(E.b, T), off(E.b, T + d), off(E.a, T + d)] };
}

/**
 * Release: a rectangle the full length of wall `vid`, `depthFt` deep (whole
 * inches), one stored wall thickness out, sharing that wall. Refused below
 * `across_min_ft`. The screen then opens the name chips.
 */
export function dragOutRoom(
  plan: Plan, rid: string, vid: number, depthFt: number, opts: { id?: string; name?: string } = {},
): { ok: true; rid: string } | Fail<'missing' | 'too-short'> {
  const src = findRoom(plan, rid);
  const E = src && edgeOf(src, vid);
  if (!src || !E) return { ok: false, err: 'missing', message: 'That wall is gone' };
  const d = snapIn(depthFt), L = dist(E.a, E.b);
  if (d < POLICY.across_min_ft || L < POLICY.across_min_ft) return { ok: false, err: 'too-short', message: 'Drag farther out' };
  const r = roomOffWall(plan, src, vid, 0, [[0, 0], [L, 0], [L, d], [0, d]], opts.name ?? POLICY.new_room_name, opts.id);
  plan.rooms.push(r);
  tidy(plan);
  return { ok: true, rid: r.id };
}
